import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { SkeletonLoader } from '../SkeletonLoader';

type Tone = 'neutral' | 'accent' | 'purple' | 'success' | 'warning' | 'amber' | 'orange' | 'danger' | 'info';

interface StatTileProps {
  label: ReactNode;
  value: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  tone?: Tone;
  /** Small line under the value (e.g. "of 120 total"). */
  hint?: ReactNode;
  /** Trend / delta text shown next to the value. */
  delta?: ReactNode;
  deltaTone?: 'success' | 'danger' | 'neutral';
  loading?: boolean;
  /** Render as a link. */
  to?: string;
  /** Render as a button. */
  onClick?: () => void;
  /** Highlight (e.g. the active filter). */
  active?: boolean;
  className?: string;
  title?: string;
}

const deltaClass: Record<NonNullable<StatTileProps['deltaTone']>, string> = {
  success: 'text-green-400',
  danger: 'text-red-400',
  neutral: 'text-glass-400',
};

/** Compact KPI tile: eyebrow label, big tabular number, optional icon tile / hint / delta. */
export const StatTile = ({
  label,
  value,
  icon: Icon,
  tone = 'neutral',
  hint,
  delta,
  deltaTone = 'neutral',
  loading = false,
  to,
  onClick,
  active = false,
  className,
  title,
}: StatTileProps) => {
  const interactive = Boolean(to || onClick);
  const body = loading ? (
    <div className="flex-1 min-w-0 space-y-2">
      <SkeletonLoader className="h-3 w-20" />
      <SkeletonLoader className="h-7 w-16" />
    </div>
  ) : (
    <div className="flex-1 min-w-0">
      <div className="eyebrow truncate">{label}</div>
      <div className="mt-1 flex items-baseline gap-2 min-w-0">
        <div className="text-2xl font-semibold tracking-tight tabular-nums text-white truncate">{value}</div>
        {delta && <span className={cn('text-xs font-medium tabular-nums', deltaClass[deltaTone])}>{delta}</span>}
      </div>
      {hint && <div className="mt-1 text-xs text-glass-500 truncate">{hint}</div>}
    </div>
  );

  const classes = cn(
    'glass-card p-4 flex items-start justify-between gap-3 text-left w-full',
    interactive && 'glass-card-interactive',
    active && 'border-accent-400/50',
    className,
  );

  const content = (
    <>
      {body}
      {Icon && (
        <span className={cn('icon-tile', `icon-tile-${tone}`)}>
          <Icon className="w-4 h-4" />
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} title={title}>
        {content}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes} title={title}>
        {content}
      </button>
    );
  }
  return (
    <div className={classes} title={title}>
      {content}
    </div>
  );
};
